/**
 * Preferencias do usuario: UMA chave de localStorage por user_id, contendo o
 * dict flat inteiro. O formato espelha o futuro `PATCH /users/me`
 * (`preferences: dict[str, string]`) — migrar pro back e trocar este adapter, o
 * dict e o mesmo. Namespaceado por `user_id` porque localStorage e do
 * navegador, nao da conta.
 */

export type Prefs = Record<string, string>

/** Prefs mudaram nesta aba — quem depende re-le. */
export const PREFS_CHANGE_EVENT = 'prefs-change'

export const MAX_PREF_ENTRIES = 50
export const MAX_PREF_KEY_LENGTH = 32
export const MAX_PREF_VALUE_LENGTH = 64

/** Slug kebab com `:` de namespace. Total <= 32 (1 + ate 31). */
const KEY_RE = /^[a-z0-9][a-z0-9:-]{0,31}$/

/**
 * Allowlist de caracteres do VALOR. `;{}<>()` ficam de fora por construcao,
 * entao `url(` e `expression(` sao impossiveis — as duas checagens explicitas
 * abaixo sao redundantes de proposito: sao o que o back vai aplicar, e manter
 * o espelho aqui evita pref que grava local e o back rejeita depois.
 */
const VALUE_RE = /^[A-Za-z0-9 #._:/-]{0,64}$/
const FORBIDDEN_SUBSTRINGS = ['url(', 'expression(']

export type PrefValidationReason = 'key' | 'value' | 'limit'

/** Erro TIPADO: a camada de UI nunca engole isto — nos consumidores desta
 *  fatia ele so dispara com bug de programacao (todo valor vem de allowlist). */
export class PrefValidationError extends Error {
  constructor(
    readonly reason: PrefValidationReason,
    message: string,
  ) {
    super(message)
    this.name = 'PrefValidationError'
  }
}

export function prefsKey(userId: string): string {
  return `prefs:${userId}`
}

function safeStorage(): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

/**
 * Fallback de sessao: quando o setItem falha (cota/modo restrito) a preferencia
 * continua valendo ate o reload, em vez de sumir sem aviso. Sai do mapa assim
 * que um write volta a persistir — memoria nunca mascara storage saudavel.
 */
const memory = new Map<string, Prefs>()

/** Só pra teste: zera o fallback entre casos. */
export function __resetPrefsMemory(): void {
  memory.clear()
}

function isPrefs(value: unknown): value is Prefs {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false
  return Object.values(value as Record<string, unknown>).every((v) => typeof v === 'string')
}

/** Leitura total e defensiva: qualquer falha ou forma inesperada devolve `{}`. */
export function readPrefs(userId: string | null | undefined): Prefs {
  if (!userId) return {}
  const cached = memory.get(userId)
  if (cached) return cached
  const storage = safeStorage()
  if (!storage) return {}
  try {
    const raw = storage.getItem(prefsKey(userId))
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    return isPrefs(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

export function readPref(userId: string | null | undefined, key: string): string | null {
  return readPrefs(userId)[key] ?? null
}

function assertKey(key: string): void {
  if (!KEY_RE.test(key)) {
    throw new PrefValidationError('key', `Chave de preferencia invalida: ${key}`)
  }
}

function assertValue(value: string): void {
  if (value.length > MAX_PREF_VALUE_LENGTH || !VALUE_RE.test(value)) {
    throw new PrefValidationError('value', 'Valor de preferencia invalido.')
  }
  const lowered = value.toLowerCase()
  if (FORBIDDEN_SUBSTRINGS.some((bad) => lowered.includes(bad))) {
    throw new PrefValidationError('value', 'Valor de preferencia invalido.')
  }
}

function persist(userId: string, next: Prefs): void {
  const storage = safeStorage()
  if (!storage) {
    memory.set(userId, next)
    return
  }
  try {
    storage.setItem(prefsKey(userId), JSON.stringify(next))
    memory.delete(userId)
  } catch {
    memory.set(userId, next)
  }
}

/** Sem `user_id` (bootstrap antes do /auth/me) e no-op: `prefs:undefined` e lixo. */
export function writePref(
  userId: string | null | undefined,
  key: string,
  value: string,
): void {
  if (!userId) return
  assertKey(key)
  assertValue(value)

  const current = readPrefs(userId)
  if (!(key in current) && Object.keys(current).length >= MAX_PREF_ENTRIES) {
    throw new PrefValidationError('limit', `Limite de ${MAX_PREF_ENTRIES} preferencias atingido.`)
  }

  persist(userId, { ...current, [key]: value })
  window.dispatchEvent(new CustomEvent(PREFS_CHANGE_EVENT))
}

/** "Restaurar padrao" por secao: apaga N chaves num write so, um evento so. */
export function removePrefs(userId: string | null | undefined, keys: readonly string[]): void {
  if (!userId) return
  const current = readPrefs(userId)
  const next: Prefs = { ...current }
  for (const key of keys) delete next[key]

  persist(userId, next)
  window.dispatchEvent(new CustomEvent(PREFS_CHANGE_EVENT))
}

/** `storage` cobre outra aba; o CustomEvent cobre esta. Devolve o unsubscribe. */
export function subscribePrefs(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(PREFS_CHANGE_EVENT, onChange)
  window.addEventListener('storage', onChange)
  return () => {
    window.removeEventListener(PREFS_CHANGE_EVENT, onChange)
    window.removeEventListener('storage', onChange)
  }
}
